import { ajouterPiece, modifierPiece } from '../api/measurementApi';
import { ajouterEnFile, listerFileTotale, marquerErreur, supprimerDeLaFile } from './db';

// Fait vivre la file d'attente hors-ligne indépendamment de tout composant React (elle doit
// continuer à se vider même si l'utilisateur a changé d'écran) : un petit pub-sub suffit, pas
// besoin d'un state manager pour une seule table.
const abonnes = new Set();

function notifier() {
  abonnes.forEach((cb) => cb());
}

export function surChangementFile(cb) {
  abonnes.add(cb);
  return () => abonnes.delete(cb);
}

let synchronisationEnCours = false;

/**
 * Ajoute une pièce à la file ET tente immédiatement l'envoi si le réseau est là. En ligne,
 * l'utilisateur ne voit donc jamais de délai — la file n'est un filet de sécurité que quand
 * `navigator.onLine` est faux ou que l'envoi immédiat échoue.
 */
export async function enregistrerPiece({ sessionId, pieceId, payload, meta }) {
  const localId = await ajouterEnFile({
    sessionId,
    pieceId: pieceId ?? null,
    mode: pieceId ? 'update' : 'create',
    payload,
    meta: meta || null,
  });
  synchroniser();
  return localId;
}

export async function nombreEnAttente(sessionId) {
  const tous = await listerFileTotale();
  return tous.filter((i) => (sessionId ? i.sessionId === sessionId : true) && !i.erreur).length;
}

export async function nombreEnErreur(sessionId) {
  const tous = await listerFileTotale();
  return tous.filter((i) => (sessionId ? i.sessionId === sessionId : true) && i.erreur).length;
}

export async function listerToutesEnAttente() {
  const tous = await listerFileTotale();
  return tous.sort((a, b) => a.creeLe - b.creeLe);
}

/**
 * Vide la file dans l'ordre d'arrivée. S'arrête au premier échec RÉSEAU (inutile d'essayer les
 * suivants, ils échoueraient pareil) mais continue après un refus MÉTIER (ex. taille absente du
 * référentiel) : cet item est marqué en erreur et reste visible dans l'écran de synchronisation,
 * les autres pièces de la file ne doivent pas rester bloquées derrière une pièce fautive.
 */
export async function synchroniser() {
  if (synchronisationEnCours || !navigator.onLine) return;
  synchronisationEnCours = true;
  try {
    const file = await listerFileTotale();
    const aTraiter = file.filter((i) => !i.erreur).sort((a, b) => a.creeLe - b.creeLe);
    for (const item of aTraiter) {
      try {
        if (item.mode === 'update') {
          await modifierPiece(item.sessionId, item.pieceId, item.payload);
        } else {
          await ajouterPiece(item.sessionId, item.payload);
        }
        await supprimerDeLaFile(item.localId);
        notifier();
      } catch (err) {
        if (err.horsLigne) {
          break; // réseau reparti hors-ligne pendant la synchro : on s'arrête, on retentera plus tard
        }
        await marquerErreur(item.localId, err.message || 'Rejetée par le serveur');
        notifier();
      }
    }
  } finally {
    synchronisationEnCours = false;
  }
}

// Synchronisation automatique : au retour du réseau, et un filet toutes les 20 s pendant qu'on
// est en ligne (au cas où le premier essai après reconnexion soit passé pendant une fenêtre trop
// courte). Pas de Background Sync API : la tablette reste ouverte sur l'app pendant la saisie,
// un intervalle simple suffit et fonctionne sur tous les navigateurs.
window.addEventListener('online', synchroniser);
setInterval(() => {
  if (navigator.onLine) synchroniser();
}, 20_000);
